import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, IconButton, LinearProgress, Link, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import MovieIcon from '@mui/icons-material/Movie';
import { useAuth } from '../contexts/AuthContext';

interface Recording {
  key: string;
  name: string;
  size: number;
  uploaded_at: string;
  /** When it was recorded — from the file's metadata; null if it was never recorded */
  captured_at: string | null;
  /** When an edited version was made from the capture */
  edited_at: string | null;
  duration_seconds: number | null;
}

const API_URL = process.env.REACT_APP_API_URL || '';

const megabytes = (bytes: number) => `${(bytes / (1024 * 1024)).toFixed(0)} MB`;
const minutes = (s: number) => `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, '0')}`;
/** In Eastern time — the team's clock — with the zone named, since the file names use UTC */
const eastern = (iso: string) =>
  new Date(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
    timeZone: 'America/New_York', timeZoneName: 'short',
  });

/**
 * Screen recordings kept in S3, admins only.
 *
 * The files are too large for the repository, so they live in the private backup bucket. Watch
 * plays one in a dialog; Download saves it. Both ask the server for a link that works for 15
 * minutes — nothing is publicly reachable — and each is written to the Activity Log.
 */
export const Recordings: React.FC = () => {
  const { token } = useAuth();
  const theme = useTheme();
  const narrow = useMediaQuery(theme.breakpoints.down('sm'));
  const [recordings, setRecordings] = useState<Recording[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [playing, setPlaying] = useState<{ recording: Recording; url: string } | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/recordings`, { headers: { Authorization: `Bearer ${token}` } });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.message || 'Could not load the recordings.');
      setRecordings(body.recordings || []);
    } catch (e: any) {
      setError(e.message);
      setRecordings([]);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  /** A fresh signed link: inline to play, attachment to save. */
  const linkFor = async (r: Recording, inline: boolean) => {
    const res = await fetch(
      `${API_URL}/api/admin/recordings/download?key=${encodeURIComponent(r.key)}${inline ? '&inline=1' : ''}`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    const body = await res.json().catch(() => ({}));
    if (!res.ok || !body.url) throw new Error(body.message || 'Could not create a link.');
    return body.url as string;
  };

  const watch = async (r: Recording) => {
    setBusy(`${r.key}:watch`);
    setError(null);
    try {
      setPlaying({ recording: r, url: await linkFor(r, true) });
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(null);
    }
  };

  const download = async (r: Recording) => {
    setBusy(`${r.key}:download`);
    setError(null);
    try {
      // The signed link goes straight to S3; the file never passes through the app
      window.location.href = await linkFor(r, false);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(null);
    }
  };

  const details = (r: Recording) => (
    <>
      {r.captured_at ? <>Captured {eastern(r.captured_at)}</> : <>Stored {eastern(r.uploaded_at)}</>}
      {r.edited_at && <> · edited {eastern(r.edited_at)}</>}
      {r.duration_seconds != null && <> · {minutes(r.duration_seconds)}</>}
      {' · '}{megabytes(r.size)}
    </>
  );

  return (
    // Full width and left-aligned like every other page. It started as a centred md Container,
    // which read as a different app (2026-10-17).
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>Recordings</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Screen recordings stored privately in S3 — too large to keep with the code. <strong>Watch</strong>{' '}
        plays one here; <strong>Download</strong> saves it. Each uses a link that stops working after 15
        minutes, and is noted in the Activity Log. The .mov plays best in Safari; the .mp4 plays anywhere.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {recordings === null && <LinearProgress />}
      {recordings && recordings.length === 0 && !error && (
        <Alert severity="info">No recordings stored yet.</Alert>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {recordings?.map(r => (
          <Card key={r.key} variant="outlined" sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <MovieIcon color="action" />
            <Box sx={{ flexGrow: 1, minWidth: 200 }}>
              <Typography variant="body1" sx={{ fontWeight: 500, wordBreak: 'break-word' }}>{r.name}</Typography>
              <Typography variant="caption" color="text.secondary">{details(r)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={busy === `${r.key}:watch` ? <CircularProgress size={16} color="inherit" /> : <PlayCircleOutlineIcon />}
                disabled={busy !== null}
                onClick={() => watch(r)}
              >
                Watch
              </Button>
              <Button
                variant="contained"
                startIcon={busy === `${r.key}:download` ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
                disabled={busy !== null}
                onClick={() => download(r)}
              >
                Download
              </Button>
            </Box>
          </Card>
        ))}
      </Box>

      <Dialog open={!!playing} onClose={() => setPlaying(null)} maxWidth="lg" fullWidth fullScreen={narrow}>
        {playing && (
          <>
            <DialogTitle sx={{ pr: 6 }}>
              {playing.recording.name}
              <Typography variant="body2" color="text.secondary">{details(playing.recording)}</Typography>
              <IconButton
                aria-label="Close"
                onClick={() => setPlaying(null)}
                sx={{ position: 'absolute', right: 8, top: 8 }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ bgcolor: '#000', p: 0, display: 'flex', justifyContent: 'center' }}>
              {/* Unmounted on close, so playback stops with it */}
              <video
                src={playing.url}
                controls
                autoPlay
                playsInline
                style={{ width: '100%', maxHeight: '75vh', display: 'block' }}
              />
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'space-between' }}>
              <Link href={playing.url} target="_blank" rel="noopener noreferrer" underline="hover" variant="body2">
                Open in a new tab <OpenInNewIcon sx={{ fontSize: 14, verticalAlign: 'middle' }} />
              </Link>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button startIcon={<DownloadIcon />} onClick={() => download(playing.recording)}>Download</Button>
                <Button variant="contained" onClick={() => setPlaying(null)}>Close</Button>
              </Box>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};
